// `StreamedSpanJSON` is not re-exported by @sentry/react or @sentry/browser, so
// it comes from @sentry/core, pinned in package.json to the version the SDK
// itself pins. `withStreamedSpan` is re-exported, but is imported from the same
// place to keep the streamed-span API coming from one module.
import { type StreamedSpanJSON, withStreamedSpan } from '@sentry/core';
import * as Sentry from '@sentry/react';
import { useEffect } from 'react';
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from 'react-router';

import { getEnvVariable } from '~/dev/utils/getEnvVariable';
import {
  getSentryClientSlug,
  normalizeTransactionClient,
  stripTrailingSlash,
} from '~/routing/utils';
import { getIsPageTearingDown } from '~/utils/pageTeardown';
import {
  recordSuppressedError,
  SuppressedErrorReason,
} from '~/utils/suppressedErrorTelemetry';

// Sentry's spans/EAP dataset backs the `transaction` dimension with the
// `sentry.segment.name` attribute; `sentry.transaction` is its deprecated
// alias, written only by the web-vital span producers. Spelled out as literals
// because @sentry/conventions (which declares them) is a transitive dependency
// of the SDK, not a direct one.
const SEGMENT_NAME_ATTRIBUTE = 'sentry.segment.name';
const TRANSACTION_ATTRIBUTE = 'sentry.transaction';
const TRANSACTION_NAME_ATTRIBUTES = [
  SEGMENT_NAME_ATTRIBUTE,
  TRANSACTION_ATTRIBUTE,
] as const;

// Resolved eagerly, at module evaluation, rather than per call.
//
// The slug is the BrowserRouter basename, which cannot change without a full
// page load — and this module is re-evaluated on every one, so reading it once
// here is exact rather than merely cheap.
//
// Eagerly and not lazily: `beforeSendSpan` runs when a span *ends*, and
// web-vital spans are flushed at pagehide or on the first navigation. Resolved
// at that point — even once and then cached — a page whose URL had since moved
// to a clientless route (previews, `folders-permission-check`) would yield ''
// and skip normalization on exactly the spans carrying the LCP/INP
// measurements, which is the bug this is guarding against.
const clientSlug = getSentryClientSlug();

// Shared by the hooks below so error events (`event.transaction`) and streamed
// spans normalize identically. `strippedClientSlug` is '' when nothing
// tenant-specific was stripped.
function normalizeSentryTransactionName(name: string): {
  name: string;
  strippedClientSlug: string;
} {
  const withoutSlug = normalizeTransactionClient(name, clientSlug);
  return {
    // Merges trailing-slash route variants (e.g. `/:client/` and `/:client`)
    // into one bucket, independent of whether a client slug was stripped above,
    // so non-tenant routes get the same treatment.
    name: stripTrailingSlash(withoutSlug),
    strippedClientSlug: withoutSlug === name ? '' : clientSlug,
  };
}

// Replaces the leading workspace/client slug (the BrowserRouter basename) with
// `:client` on every error event we send to Sentry.
//
// Why: the slug is part of the name/transaction on each event, so the same page
// or error on `/nonga/...` vs `/acme/...` is recorded as distinct transactions
// and carries a tenant-specific `transaction` value. That fragments Performance
// and Web Vitals (LCP, INP, ...) per tenant AND fragments per-page error
// aggregation. Normalizing to `/:client/...` makes both aggregate across
// tenants. The original slug is preserved as the `client_workspace` tag so
// per-tenant filtering still works.
//
// Scope note: this normalizes the `transaction` dimension only. Sentry error
// issue grouping is fingerprint-based (stacktrace), so identical errors already
// group across tenants regardless of this change — we are de-duplicating the
// transaction label used for filtering/aggregation, not altering issue
// grouping.
//
// The slug comes from getSentryClientSlug(), which returns '' for routes with no
// real tenant prefix (login family, folders-permission-check, /redirects/evisync,
// previews) so those keep their real names instead of collapsing into /:client.
export function stripClientSlugFromEvent(
  event: Sentry.ErrorEvent,
): Sentry.ErrorEvent {
  if (!event.transaction) return event;

  const { name, strippedClientSlug } = normalizeSentryTransactionName(
    event.transaction,
  );
  event.transaction = name;
  if (strippedClientSlug) {
    event.tags = { ...event.tags, client_workspace: strippedClientSlug };
  }
  return event;
}

// Same normalization for streamed spans, applied to the transaction-name
// *attributes* rather than to `span.name`.
//
// `span.name` is not the transaction dimension. `captureSpan` snapshots
// `sentry.segment.name` off the segment span before `beforeSendSpan` runs, and
// the helper it uses to write attributes skips keys that are already set — so
// rewriting `name` alone lands on a field nothing queries.
//
// The attributes are rewritten on every span, not only segments. A child span
// carries its segment's name, and the web-vital producers (LCP, CLS, INP) set
// `sentry.segment.name` themselves from the un-normalized scope transaction
// name. Those child spans are where the web-vital measurements live in stream
// mode, so a segment-only rewrite would leave every web vital fragmented per
// tenant. Their own `name` describes an element rather than a route, so `name`
// is still only rewritten on segments.
//
// The slug goes in `attributes` rather than `tags` — scope tags are not applied
// to spans in stream mode.
export function stripClientSlugFromSpan(
  span: StreamedSpanJSON,
): StreamedSpanJSON {
  const attributes = { ...span.attributes };
  let strippedClientSlug = '';
  let changed = false;

  for (const attribute of TRANSACTION_NAME_ATTRIBUTES) {
    const value = attributes[attribute];
    // Absent (`sentry.transaction` is only set by the web-vital producers, and
    // must never be added where it is missing) or not a bare string.
    if (typeof value !== 'string') continue;

    const normalized = normalizeSentryTransactionName(value);
    if (normalized.name === value) continue;

    // Assign a plain string: the SDK drops attribute values it cannot type as
    // primitives, which would blank the transaction dimension rather than fix it.
    attributes[attribute] = normalized.name;
    strippedClientSlug ||= normalized.strippedClientSlug;
    changed = true;
  }

  let name = span.name;
  if (span.is_segment && name) {
    const normalized = normalizeSentryTransactionName(name);
    name = normalized.name;
    strippedClientSlug ||= normalized.strippedClientSlug;
    changed ||= name !== span.name;
  }

  if (!changed) return span;

  // Segment spans only: `client_workspace` is a per-transaction dimension, and
  // repeating it across every child span would spend the SDK's per-trace byte
  // budget without making anything newly queryable.
  if (span.is_segment && strippedClientSlug) {
    attributes.client_workspace = strippedClientSlug;
  }

  return { ...span, name, attributes };
}

function addScreenResolutionToSpan(span: StreamedSpanJSON): StreamedSpanJSON {
  if (!span.is_segment) return span;

  return {
    ...span,
    attributes: {
      ...span.attributes,
      screen_width_pixels: window.screen.width,
      screen_height_pixels: window.screen.height,
    },
  };
}

// Returns true when every stack frame in the event comes from an anonymous /
// eval'd script (filename is '<anonymous>', absent, or the literal string
// 'eval'). This is the fingerprint of errors injected by browser extensions
// running in the page's main world — they are not actionable from our side.
const isFromEvaledCode = (event: Sentry.ErrorEvent): boolean => {
  const exceptions = event.exception?.values;
  if (!exceptions?.length) return false;

  return exceptions.every((ex) => {
    const frames = ex.stacktrace?.frames;
    if (!frames?.length) return false;
    return frames.every((frame) => {
      const filename = frame.filename ?? '';
      return (
        filename === '' ||
        filename === '<anonymous>' ||
        filename === 'eval' ||
        filename.startsWith('eval at ')
      );
    });
  });
};

// One pattern per engine: Chrome, Firefox, Safari.
const DYNAMIC_IMPORT_FAILURE_PATTERN =
  /failed to fetch dynamically imported module|error loading dynamically imported module|importing a module script failed/i;

// An aborted import and a missing chunk reject identically, and assigning
// `window.location` aborts every in-flight module fetch. Teardown is the only
// case we can attribute to navigation; on a live page the failure is real.
const isImportAbortedByNavigation = (event: Sentry.ErrorEvent): boolean => {
  if (!getIsPageTearingDown()) return false;

  return (
    event.exception?.values?.some((exception) =>
      DYNAMIC_IMPORT_FAILURE_PATTERN.test(exception.value ?? ''),
    ) ?? false
  );
};

const getFailedModuleUrl = (event: Sentry.ErrorEvent): string => {
  const values = event.exception?.values ?? [];
  for (const exception of values) {
    const match = /(https?:\/\/\S+)/.exec(exception.value ?? '');
    if (match) return match[1];
  }
  return 'unknown';
};

export const initSentry = (): void => {
  Sentry.init({
    enabled: true,
    dsn: getEnvVariable('SENTRY_DSN'),
    environment: getEnvVariable('SENTRY_ENVIRONMENT'),
    _experiments: { enableLogs: true },
    beforeSend: (event) => {
      if (isFromEvaledCode(event)) return null;
      if (isImportAbortedByNavigation(event)) {
        recordSuppressedError(
          SuppressedErrorReason.ImportErrorDroppedDuringTeardown,
          { module: getFailedModuleUrl(event) },
        );
        return null;
      }
      return stripClientSlugFromEvent(event);
    },
    // Replaces `beforeSendTransaction`, which does not exist in stream mode.
    // `withStreamedSpan` is not optional — spanStreamingIntegration silently
    // downgrades the client back to static traces without it.
    beforeSendSpan: withStreamedSpan((span) =>
      addScreenResolutionToSpan(stripClientSlugFromSpan(span)),
    ),
    integrations: [
      // React Router v7 variant of browserTracingIntegration: hooks into the
      // router so navigations emit transactions named after the matched route
      // template (e.g. `/documents/:documentId`) rather than the raw URL.
      // Pairs with withSentryReactRouterV7Routing in src/components/Router/Router.tsx.
      Sentry.reactRouterV7BrowserTracingIntegration({
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
        shouldCreateSpanForRequest: (url) => {
          return !url.includes('sockjs-mt1.pusher.com');
        },
        enableInp: true,
      }),
      // Sets `traceLifecycle: 'stream'`. Static mode buffers a whole trace in
      // browser memory and only sends it when the root span ends, so long
      // sessions lost their root span when the tab closed first and left the
      // pilot-side children orphaned. Streaming flushes every ~5s instead.
      // Requires >= 10.66.0; default in v11.
      Sentry.spanStreamingIntegration(),
      // NOTE: browserProfilingIntegration and replayIntegration are registered
      // after boot by addDeferredSentryIntegrations (see
      // ./sentryDeferredIntegrations), not here. They are the two heaviest parts
      // of the SDK and were loading and starting before first render. Because
      // browserProfilingIntegration is absent at init, profilesSampleRate does
      // not apply to the pageload transaction — that is the accepted cost.
      // Auto-captures failed Evisort API requests as Sentry events so we don't
      // have to manually wrap each call site in captureException.
      //
      // Scoped to 5xx only on purpose: 4xx responses (401 expired session, 403
      // RBAC denial, 404 deleted resource, 423 polled-endpoint lock, 422 form
      // validation, 429 rate limit) are expected business outcomes and bury
      // actual server failures under tens of thousands of non-actionable events.
      Sentry.httpClientIntegration({
        failedRequestStatusCodes: [[500, 599]],
        failedRequestTargets: [/evisort\.(com|dev)/],
      }),
      // Deep-serializes nested error properties (e.g. Axios `response`/`config`)
      // so they show up as readable JSON instead of `[object Object]`.
      Sentry.extraErrorDataIntegration({ depth: 5 }),
      // Adds ±5 lines of source context to each stack frame using the sourcemaps
      // we already upload, so issues are readable without opening the repo.
      Sentry.contextLinesIntegration({ frameContextLines: 5 }),
      // Tags each stack frame with its bundle chunk metadata, which helps group
      // errors by code-split chunk and route ownership in Sentry.
      Sentry.moduleMetadataIntegration(),
      // Attaches the active LaunchDarkly flag set to error events. Pairs with the
      // buildLaunchDarklyFlagUsedHandler inspector wired into FlagsProvider.
      Sentry.launchDarklyIntegration(),
      // Browser interventions and crashes never reach a JS error handler.
      // `deprecation` is excluded — noisy, and mostly third-party.
      Sentry.reportingObserverIntegration({
        types: ['crash', 'intervention'],
      }),
    ],
    // `enableTracing` was removed in @sentry/react v8+; tracesSampleRate alone
    // controls whether tracing fires now.
    sendDefaultPii: true,

    // DO NOT CHANGE THIS VALUE unless you know what you are doing.
    tracesSampleRate: Number(getEnvVariable('SENTRY_TRACES_SAMPLE_RATE')),
    tracePropagationTargets: [
      /^https:\/\/(?!editor\.)(?!auth\.)[\w.]+evisort\.(com|dev)/,
    ],

    // DO NOT CHANGE THIS VALUE unless you know what you are doing.
    // Session Replay
    replaysSessionSampleRate: Number(
      getEnvVariable('SENTRY_REPLAYS_SESSION_SAMPLE_RATE'),
    ),
    replaysOnErrorSampleRate: Number(
      getEnvVariable('SENTRY_REPLAYS_ONERROR_SAMPLE_RATE'),
    ),

    // DO NOT CHANGE THIS VALUE unless you know what you are doing.
    profilesSampleRate: Number(getEnvVariable('SENTRY_PROFILES_SAMPLE_RATE')),

    // according to documentation, the plugin already injects it
    maxValueLength: 8192,
    normalizeDepth: 10,
    denyUrls: [
      // Chrome extensions
      /^chrome:\/\//i,
      /^chrome-extension:\/\//i,
      // Candu third-party SDK — its fetch failures are not actionable from our
      // side and produced ~3k events / 14d at the time of writing.
      /candu\.ai/i,
    ],
    // https://sentry.io/answers/react-resizeobserver-loop-completed-with-undelivered-notifications/
    ignoreErrors: [
      'ResizeObserver loop limit exceeded',
      'ResizeObserver loop completed with undelivered notifications.',
      // Silence Axios Network Errors
      'Network Error',
      // Catches all variants of the Candu SDK's fetch errors regardless of which
      // browser produced them ("Failed to fetch", "Load failed", "NetworkError…").
      /api\.candu\.ai/i,
    ],
  });
};
